import type { NextPageWithLayout } from 'types/page';
import {
  DEFAULT_SIDEBAR_LAYOUT_PROPS,
  withSidebarLayout,
} from 'layouts/SidebarPage/withSidebarLayout';
import { GetStaticProps, GetStaticPaths, InferGetStaticPropsType } from 'next';
import { newsDetail, newsSlugs, recentNews } from 'services/server/news/reader';
import { serializeJsonLd } from 'utils/shared/jsonLd/serialize';
import NewsDetailComponent from 'screens/News/Slug';
import { NewsDetailProps } from 'screens/News/types';
import { generateArticleJsonLd } from 'utils/shared/jsonLd/article';
import { getNewsPage } from 'constants/pages';
import { newsPageProof } from 'services/server/news/pageProof';
import type { NewsPageProof } from 'constants/payloads/news';

const HOURS_TO_SECONDS = 3600;
const REVALIDATION_INTERVAL =
  process.env.TARGET_PLATFORM === 'MOBILE' ? undefined : 48 * HOURS_TO_SECONDS;
const IS_PROD_RENDER_ONLY = false;

export const getStaticProps: GetStaticProps<
  NewsDetailProps & { newsProof: NewsPageProof },
  { slug: string }
> = async (context) => {
  if (!context.params || typeof context.params.slug !== 'string') {
    return {
      notFound: true,
      revalidate: REVALIDATION_INTERVAL,
    };
  }

  const slug = context.params.slug;

  const data = await newsDetail(slug);

  if (!data) {
    return {
      notFound: true,
      revalidate: REVALIDATION_INTERVAL,
    };
  }

  if ('redirect' in data) return { redirect: { destination: data.redirect, permanent: true } };
  const recentArticles = await recentNews(slug);

  const blogPostJsonLd = generateArticleJsonLd({
    headline: data.title || '',
    datePublished: data.publishedAt || '',
    description: data?.seoMeta?.metaDescription || '',
    author: {
      name: data.authorName || 'InsiderFinance Editorial',
    },
    image: data.featuredImageUrl
      ? [
          new URL(
            data.featuredImageUrl,
            process.env.APP_URL || 'https://www.insiderfinance.io',
          ).toString(),
        ]
      : [],
    /**
     * @note need to transform from portable text for this to work
     */
    // articleBody: data.body,
    publisher: {
      name: 'InsiderFinance',
      logo: 'https://www.insiderfinance.io/icons/apple-icon.png',
    },
    mainEntityOfPage: 'https://www.insiderfinance.io' + getNewsPage(data?.slug?.current || ''),
  });

  return {
    props: {
      data,
      jsonLd: serializeJsonLd(blogPostJsonLd),
      recentArticles: recentArticles || [],
      newsProof: newsPageProof({ data, recentArticles: recentArticles || [] }),
    },
    revalidate: REVALIDATION_INTERVAL,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (
    IS_PROD_RENDER_ONLY &&
    (process.env.APP_STAGE === 'staging' || process.env.APP_STAGE === 'beta')
  ) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  const slugs = await newsSlugs();

  const paths = slugs?.map((item) => {
    return {
      params: { slug: item.slug || '' },
    };
  });

  return {
    paths,
    fallback: 'blocking',
  };
};

const NewsDetailsComponent = (props: InferGetStaticPropsType<typeof getStaticProps>) => {
  return <NewsDetailComponent {...props} />;
};

(NewsDetailsComponent as NextPageWithLayout<NewsDetailProps>).getLayout = withSidebarLayout(
  DEFAULT_SIDEBAR_LAYOUT_PROPS,
);

export default NewsDetailsComponent;
